import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { Link } from '../router';
import { Bag, Books, Home } from '../icons';
import { useT } from '../i18n';
import { load, KEYS } from '../storage';
import { useGroceryCount } from '../grocery-selection';
import type { Profile } from '../types';

export function Wordmark() {
  const t = useT();
  return <Link href="/" class="wordmark" aria-label={t('nav.home.label')}>eat<span>.</span>recipe</Link>;
}

export type Tab = 'home' | 'albums' | 'grocery' | '';

export function BottomNav({ current }: { current: Tab }) {
  const t = useT();
  const n = useGroceryCount();
  const item = (key: Tab, href: string, label: string, icon: ComponentChildren, badge = 0) => (
    <Link href={href} aria-current={current === key ? 'page' : undefined} aria-label={badge ? `${label}, ${t('n.recipes', { n: badge })}` : undefined}>
      <span class="nav-icon">{icon}{badge > 0 && <span class="badge-count" aria-hidden="true">{badge}</span>}</span>{label}
    </Link>
  );
  return (
    <nav class="bottom-nav" aria-label={t('nav.main')}>
      <div class="bottom-nav-inner">
        {item('home', '/', t('nav.home'), <Home />)}
        {item('albums', '/albums', t('nav.albums'), <Books />)}
        {item('grocery', '/grocery', t('nav.grocery'), <Bag />, n)}
      </div>
    </nav>
  );
}

/** A round grocery button above the bottom bar, shown once the list has recipes on it. */
export function GroceryFab() {
  const t = useT();
  const n = useGroceryCount();
  if (n === 0) return null;
  return (
    <Link href="/grocery" class="grocery-fab" aria-label={`${t('nav.grocery')}, ${t('n.recipes', { n })}`}>
      <Bag size={24} /><span class="badge-count" aria-hidden="true">{n}</span>
    </Link>
  );
}

/** The profile button (initial on turmeric-soft) that opens Settings. */
export function Avatar() {
  const t = useT();
  const me = load<Profile | null>(KEYS.me, null);
  return <Link href="/settings" class="avatar-btn" aria-label={t('common.profile')}>{me?.initial || '•'}</Link>;
}

/** A short-lived status message; announce politely to screen readers. */
export function useToast(): [ComponentChildren, (msg: string) => void] {
  const [msg, setMsg] = useState('');
  useEffect(() => {
    if (!msg) return;
    const id = setTimeout(() => setMsg(''), 2600);
    return () => clearTimeout(id);
  }, [msg]);
  const node = msg ? <div class="toast" role="status" aria-live="polite">{msg}</div> : null;
  return [node, setMsg];
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const t = useT();
  return (
    <div class="card" role="alert" style="padding:16px;display:flex;flex-direction:column;gap:10px;margin:16px">
      <div style="font-weight:700">{message}</div>
      {onRetry && <button class="btn btn-outline" style="min-height:46px" onClick={onRetry}>{t('common.tryAgain')}</button>}
    </div>
  );
}

/** Section heading with an optional link on the right. */
export function SectionHead({ title, link, hint }: { title: string; link?: { href: string; label: string }; hint?: string }) {
  return (
    <div class="section-head">
      <h2 class="h2">{title}</h2>
      {link ? <Link href={link.href} class="section-link">{link.label}</Link> : hint ? <span class="small muted" style="text-align:right">{hint}</span> : null}
    </div>
  );
}
